import { useRef, useState, useEffect, useCallback } from "react";
import { X, Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CameraScannerProps {
  onCapture: (imageData: string) => void;
  onClose: () => void;
  instructions: {
    placePassport: string;
    moveCloser: string;
    moreLightNeeded: string;
    holdSteady: string;
    capture: string;
  };
}

type InstructionState = "place" | "closer" | "light" | "steady";

export function CameraScanner({ onCapture, onClose, instructions }: CameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analysisCanvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [instructionState, setInstructionState] = useState<InstructionState>("place");
  const [isCapturing, setIsCapturing] = useState(false);
  const previousFrameRef = useRef<ImageData | null>(null);
  const motionCountRef = useRef(0);
  const analysisIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start camera on mount
  useEffect(() => {
    const startCamera = async () => {
      try {
        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Camera not supported by this browser");
        }

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        
        setStream(mediaStream);
        
        if (videoRef.current) {
          const video = videoRef.current;
          video.srcObject = mediaStream;
          
          // Wait for video metadata to load before playing
          await new Promise<void>((resolve, reject) => {
            const onLoadedMetadata = () => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = () => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              reject(new Error('Video failed to load'));
            };
            
            video.addEventListener('loadedmetadata', onLoadedMetadata);
            video.addEventListener('error', onError);
            
            // If metadata is already loaded, resolve immediately
            if (video.readyState >= 1) {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              resolve();
            }
          });
          
          // Now play the video
          await video.play();
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Camera access error:", err);
        
        // Provide specific error messages
        let errorMessage = "Could not access camera. ";
        
        if (err instanceof Error) {
          if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
            errorMessage += "Please allow camera access in your browser settings.";
          } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
            errorMessage += "No camera device found.";
          } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
            errorMessage += "Camera is already in use by another app.";
          } else if (err.name === "OverconstrainedError" || err.name === "ConstraintNotSatisfiedError") {
            errorMessage += "Camera constraints not supported.";
          } else if (err.name === "TypeError") {
            errorMessage += "Camera access not supported on this device.";
          } else if (err.message) {
            errorMessage += err.message;
          } else {
            errorMessage += "Please check your camera settings.";
          }
        }
        
        setError(errorMessage);
        setIsLoading(false);
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
    };
  }, []);

  // Analyze video frames for brightness and motion
  useEffect(() => {
    if (!videoRef.current || isLoading || error) return;

    const analyzeFrame = () => {
      const video = videoRef.current;
      const canvas = analysisCanvasRef.current;
      if (!video || !canvas || video.videoWidth === 0) return;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      // Use small canvas for performance
      canvas.width = 64;
      canvas.height = 48;
      ctx.drawImage(video, 0, 0, 64, 48);

      const imageData = ctx.getImageData(0, 0, 64, 48);
      const data = imageData.data;

      // Calculate average brightness
      let totalBrightness = 0;
      for (let i = 0; i < data.length; i += 4) {
        totalBrightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }
      const avgBrightness = totalBrightness / (data.length / 4);

      // Calculate motion by comparing with previous frame
      let motionScore = 0;
      if (previousFrameRef.current) {
        const prevData = previousFrameRef.current.data;
        for (let i = 0; i < data.length; i += 4) {
          const diff = Math.abs(data[i] - prevData[i]) +
            Math.abs(data[i + 1] - prevData[i + 1]) +
            Math.abs(data[i + 2] - prevData[i + 2]);
          motionScore += diff;
        }
        motionScore = motionScore / (data.length / 4);
      }
      previousFrameRef.current = imageData;

      // Update motion stability counter
      if (motionScore < 15) {
        motionCountRef.current = Math.min(motionCountRef.current + 1, 10);
      } else {
        motionCountRef.current = Math.max(motionCountRef.current - 2, 0);
      }

      // Determine instruction state based on analysis
      if (avgBrightness < 40) {
        setInstructionState("light");
      } else if (motionScore > 25) {
        setInstructionState("steady");
        motionCountRef.current = 0;
      } else if (avgBrightness < 80) {
        // Low but acceptable light, might be too far
        setInstructionState("closer");
      } else {
        setInstructionState("place");
      }
    };

    analysisIntervalRef.current = setInterval(analyzeFrame, 200);

    return () => {
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
    };
  }, [isLoading, error]);

  // Cleanup stream when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const handleCapture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || isCapturing) return;

    setIsCapturing(true);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL("image/jpeg", 0.92);

      // Stop camera
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      onCapture(imageData);
    }
  }, [stream, onCapture, isCapturing]);

  const handleClose = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    onClose();
  }, [stream, onClose]);

  const getInstructionText = () => {
    switch (instructionState) {
      case "closer":
        return instructions.moveCloser;
      case "light":
        return instructions.moreLightNeeded;
      case "steady":
        return instructions.holdSteady;
      default:
        return instructions.placePassport;
    }
  };

  const getInstructionColor = () => {
    switch (instructionState) {
      case "light":
        return "text-amber-400";
      case "steady":
        return "text-amber-400";
      case "closer":
        return "text-blue-400";
      default:
        return "text-white";
    }
  };

  // Passport frame dimensions (roughly 1.42:1 landscape ratio for passport data page)
  // Frame takes about 85% of screen width on mobile
  const frameAspect = 1.42;

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col">
      {/* Hidden canvases for capture and analysis */}
      <canvas ref={canvasRef} className="hidden" />
      <canvas ref={analysisCanvasRef} className="hidden" />

      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-4 left-4 z-20 w-12 h-12 flex items-center justify-center bg-black/50 rounded-full text-white active:bg-black/70 safe-area-inset-top"
        aria-label="Close camera"
      >
        <X className="h-7 w-7" />
      </button>

      {/* Main camera view */}
      <div className="flex-1 relative overflow-hidden">
        {/* Loading state */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
            <div className="text-center text-white">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
              <p className="text-lg">Starting camera...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10 p-6">
            <div className="text-center text-white">
              <p className="text-lg mb-4">{error}</p>
              <Button onClick={handleClose} variant="outline" className="text-white border-white bg-transparent">
                Go Back
              </Button>
            </div>
          </div>
        )}

        {/* Video feed */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Dimmed overlay with clear passport frame */}
        {!isLoading && !error && (
          <div className="absolute inset-0 pointer-events-none">
            {/* SVG mask for dimmed overlay with clear center */}
            <svg className="absolute inset-0 w-full h-full">
              <defs>
                <mask id="passportMask">
                  {/* White = visible (dimmed), Black = hidden (clear) */}
                  <rect width="100%" height="100%" fill="white" />
                  {/* Clear rectangle in center - passport shape */}
                  <rect
                    x="7.5%"
                    y="50%"
                    width="85%"
                    height="0"
                    fill="black"
                    rx="8"
                    style={{
                      // Calculate height based on width and aspect ratio
                      // Frame is 85% of width, height = width / aspectRatio
                      height: `calc(85vw / ${frameAspect})`,
                      transform: `translateY(calc(-85vw / ${frameAspect} / 2))`,
                    }}
                  />
                </mask>
              </defs>
              {/* Dimmed overlay */}
              <rect
                width="100%"
                height="100%"
                fill="rgba(0, 0, 0, 0.6)"
                mask="url(#passportMask)"
              />
            </svg>

            {/* Passport frame border */}
            <div
              className="absolute left-[7.5%] top-1/2 w-[85%] border-2 border-white rounded-lg"
              style={{
                aspectRatio: frameAspect,
                transform: "translateY(-50%)",
              }}
            >
              {/* Corner accents for better visibility */}
              <div className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
              <div className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
              <div className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
              <div className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
            </div>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="bg-black/80 px-6 py-6 safe-area-inset-bottom">
        {/* Instruction text */}
        <p
          className={`text-center text-xl font-semibold mb-6 transition-colors duration-300 ${getInstructionColor()}`}
        >
          {getInstructionText()}
        </p>

        {/* Capture button */}
        <div className="flex justify-center">
          <button
            onClick={handleCapture}
            disabled={isLoading || !!error || isCapturing}
            className="relative w-20 h-20 rounded-full bg-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
            aria-label={instructions.capture}
          >
            {/* Inner circle */}
            <div className="w-16 h-16 rounded-full border-4 border-black/20 flex items-center justify-center">
              {isCapturing ? (
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              ) : (
                <Camera className="h-8 w-8 text-primary" />
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
